import { CSSProperties } from "react";

interface UseSidebarStylesOptions {
  position: "absolute" | "relative";
  isOpen: boolean;
  sidebarWidth: number;
  isCustomWidth: boolean;
  minWidth: number;
  maxWidth: number;
  isResizing: boolean;
  className: string;
}

interface UseSidebarStylesReturn {
  containerClasses: string;
  containerStyle: CSSProperties;
}

const useSidebarStyles = ({
  position,
  isOpen,
  sidebarWidth,
  isCustomWidth,
  minWidth,
  maxWidth,
  isResizing,
  className,
}: UseSidebarStylesOptions): UseSidebarStylesReturn => {
  const baseClasses =
    position === "absolute"
      ? "absolute right-0 top-0 z-[2] h-full bg-white shadow-md transition-transform duration-300 ease-in-out overflow-visible"
      : "relative h-full bg-white shadow-lg overflow-visible";

  const getTransformClasses = (): string => {
    if (position !== "absolute") {
      return "";
    }

    return isOpen ? "translate-x-0" : "translate-x-full";
  };

  const getFlexClasses = (): string => {
    if (position !== "relative") {
      return "";
    }

    if (isResizing) {
      return "";
    }

    return "transition-[width,flex,min-width,max-width] duration-300 ease-in-out";
  };

  const transformClasses = getTransformClasses();
  const flexClasses = getFlexClasses();

  const containerClasses = `${baseClasses} ${transformClasses} ${flexClasses} ${className} flex flex-col overflow-hidden`;

  const getWidth = (): string | number | undefined => {
    if (!isOpen) {
      return 0;
    }

    if (isCustomWidth) {
      return sidebarWidth;
    }

    return undefined;
  };

  const getContainerStyle = (): CSSProperties => {
    if (position === "absolute") {
      return { width: sidebarWidth };
    }

    return {
      width: getWidth(),
      minWidth: isOpen ? minWidth : 0,
      maxWidth: isOpen ? maxWidth : 0,
      flexShrink: isOpen && isCustomWidth ? 0 : undefined,
    };
  };

  const containerStyle = getContainerStyle();

  return {
    containerClasses,
    containerStyle,
  };
};

export default useSidebarStyles;
